import { useCallback } from "react"

import { scannedCodeSchema } from "../schemas/scannedCodeSchema"

import { useGetPalletByReferenceId } from "./useGetPalletByReferenceId"
import { useScannerDetection } from "./useScannerDetection"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseStoredPalletScanningProps {
  onPalletScanned: (pallet: GoodsReceiptPallet) => void
}

export const useStoredPalletScanning = ({ onPalletScanned }: UseStoredPalletScanningProps): void => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { fetchPallet } = useGetPalletByReferenceId()

  const handleScan = useCallback(
    async (result: { error: boolean; success: { scannedCode: unknown } | null }) => {
      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Invalid or unrecognized pallet QR code.",
        })
        return
      }

      if (result.success) {
        const validation = scannedCodeSchema.safeParse(result.success.scannedCode)

        if (!validation.success) {
          notify({
            type: NotificationType.Error,
            silent: false,
            message: "Invalid or unrecognized pallet QR code.",
          })
          return
        }

        const scannedPallet = await fetchPallet(Number(validation.data.refId))

        if (!scannedPallet) {
          return
        }

        if (!scannedPallet.isStored) {
          notify({
            type: NotificationType.Error,
            silent: false,
            message: "This pallet has not been stored yet. Please scan a stored pallet.",
          })
          return
        }

        onPalletScanned(scannedPallet)
      }
    },
    [fetchPallet, notify, onPalletScanned]
  )

  useScannerDetection({
    onScan: (result) => void handleScan(result),
  })
}
